import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Search, X, User, Image, Calendar } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";

export function SearchDropdown({ isOpen, onClose, showSolid }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ artists: [], artworks: [], events: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setQuery("");
      setResults({ artists: [], artworks: [], events: [] });
    }
  }, [isOpen]);

  useEffect(() => {
    if (query.length < 2) {
      setResults({ artists: [], artworks: [], events: [] });
      return;
    }

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(query)}`);
        setResults(r.data);
      } catch {
        setResults({ artists: [], artworks: [], events: [] });
      }
      setLoading(false);
    }, 250);

    return () => clearTimeout(timerRef.current);
  }, [query]);

  const hasResults = results.artists.length > 0 || results.artworks.length > 0 || results.events.length > 0;

  const handleNavigate = () => {
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-40"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute top-full right-0 mt-2 w-[420px] max-h-[70vh] bg-white shadow-xl border border-[#0A1128]/10 overflow-hidden z-50"
            data-testid="search-dropdown"
          >
            {/* Input */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-[#0A1128]/10">
              <Search size={16} className="text-[#8A94A6] flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search artists, artworks, events..."
                className="flex-1 text-sm text-[#0A1128] placeholder-[#8A94A6] outline-none bg-transparent"
                data-testid="search-input"
              />
              {query && (
                <button onClick={() => setQuery("")} className="text-[#8A94A6] hover:text-[#0A1128]">
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Results */}
            <div className="overflow-y-auto max-h-[calc(70vh-60px)]">
              {query.length >= 2 && loading && (
                <div className="px-5 py-8 text-center">
                  <div className="w-5 h-5 border-2 border-[#0A1128] border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              )}

              {query.length >= 2 && !loading && !hasResults && (
                <div className="px-5 py-8 text-center">
                  <p className="text-xs text-[#8A94A6]">No results found for "{query}"</p>
                </div>
              )}

              {query.length >= 2 && !loading && hasResults && (
                <div>
                  {/* Artists */}
                  {results.artists.length > 0 && (
                    <div className="py-3">
                      <p className="px-5 text-[10px] uppercase tracking-[0.2em] text-[#8A94A6] mb-2">Artists</p>
                      {results.artists.map((artist) => (
                        <Link
                          key={artist.id}
                          to={`/artists/${artist.slug}`}
                          onClick={handleNavigate}
                          className="flex items-center gap-3 px-5 py-2.5 hover:bg-[#F8F9FA] transition-colors"
                          data-testid={`search-artist-${artist.slug}`}
                        >
                          <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 bg-[#F8F9FA]">
                            {artist.photo ? (
                              <img src={artist.photo} alt={artist.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center"><User size={14} className="text-[#8A94A6]" /></div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm text-[#0A1128] truncate">{artist.name}</p>
                            <p className="text-xs text-[#8A94A6] truncate">{artist.medium}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}

                  {/* Artworks */}
                  {results.artworks.length > 0 && (
                    <div className="py-3 border-t border-[#0A1128]/5">
                      <p className="px-5 text-[10px] uppercase tracking-[0.2em] text-[#8A94A6] mb-2">Artworks</p>
                      {results.artworks.map((artwork) => (
                        <Link
                          key={artwork.id}
                          to={`/artists/${artwork.artist_slug}`}
                          onClick={handleNavigate}
                          className="flex items-center gap-3 px-5 py-2.5 hover:bg-[#F8F9FA] transition-colors"
                          data-testid={`search-artwork-${artwork.id}`}
                        >
                          <div className="w-9 h-11 overflow-hidden flex-shrink-0 bg-[#F8F9FA]">
                            {artwork.image ? (
                              <img src={artwork.image} alt={artwork.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center"><Image size={14} className="text-[#8A94A6]" /></div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm text-[#0A1128] truncate">{artwork.title}</p>
                            <p className="text-xs text-[#8A94A6] truncate">{artwork.artist_name}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}

                  {/* Events */}
                  {results.events.length > 0 && (
                    <div className="py-3 border-t border-[#0A1128]/5">
                      <p className="px-5 text-[10px] uppercase tracking-[0.2em] text-[#8A94A6] mb-2">Events</p>
                      {results.events.map((event) => (
                        <Link
                          key={event.id}
                          to={`/events/${event.id}`}
                          onClick={handleNavigate}
                          className="flex items-center gap-3 px-5 py-2.5 hover:bg-[#F8F9FA] transition-colors"
                          data-testid={`search-event-${event.id}`}
                        >
                          <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 bg-[#F8F9FA]">
                            <Calendar size={14} className="text-[#8A94A6]" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm text-[#0A1128] truncate">{event.title}</p>
                            <p className="text-xs text-[#8A94A6] truncate">{event.date}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
